import { IFormGroup } from '../definitions/FormGroup'
import { IChangelog, IDependency, ITask, IWorkFlow } from '../definitions/Workflow'
import Helper from '../helpers/Helper'

class WorkflowManagerClass {
    isWorkingDay = (date: Date) => {
        const dayOfWeek = date.getDay()
        return dayOfWeek !== 0 && dayOfWeek !== 6
    }

    cloneWorkflow = (workflow: IWorkFlow) => {
        const now = new Date()
        const taskUuidMapping: Record<string, string> = {}
        const clonedWorkflow: IWorkFlow = JSON.parse(JSON.stringify(workflow))

        clonedWorkflow.uuid = Helper.generateUUID('workflow')
        clonedWorkflow.changelogs = []
        clonedWorkflow.comments = []
        clonedWorkflow.completedAt = ''
        clonedWorkflow.createdAt = now.toISOString()
        clonedWorkflow.status = 'open'
        clonedWorkflow.title = `${workflow.title} - Copy`
        clonedWorkflow.hasBeenScheduled = false
        clonedWorkflow.tasks = clonedWorkflow.tasks.map(task => {
            const newUuid = Helper.generateUUID('task')
            taskUuidMapping[task.uuid] = newUuid
            return {
                ...task,
                uuid: newUuid,
                comments: [],
                completedAt: now.toISOString(),
                status: 'open',
                startedAt: workflow.createdAt
            }
        }
        )
        clonedWorkflow.tasks = clonedWorkflow.tasks.map(task => {
            const updatedDepependencies: IDependency[] = task?.dependencies?.map(dep => {
                const updatedDep = {
                    ...dep,
                    uuid: taskUuidMapping[dep.uuid] || dep.uuid
                }
                return updatedDep
            })
            const updatedTask: ITask = {
                ...task,
                dependencies: updatedDepependencies
            }
            return updatedTask
        })
        return clonedWorkflow
    }

    createChangelog = (changelog: IChangelog): IChangelog => {
        return {
            uuid: Helper.generateUUID('log_'),
            createdAt: changelog.createdAt,
            user: changelog.user,
            activity: changelog.activity,
            activityType: changelog.activityType,
            before: changelog.before,
            after: changelog.after
        }
    }

    getColorFromDate = (data: string): ('success' | 'default' | 'warning' | 'error') => {
        if ((data.includes('second') || data.includes('minute') || data.includes('hours')) && data.includes('in'))
            return 'warning'
        else if (data.includes('ago'))
            return 'error'
        else return 'success'
    }

    createNewWorkflow = (): IWorkFlow => {
        return {
            uuid: Helper.generateUUID('workflow'),
            title: '',
            contract: { uuid: null, id: 0, name: '' },
            assignee: null,
            tasks: [],
            changelogs: [],
            createdAt: new Date().toISOString(),
            dueDate: new Date().toISOString(),
            completedAt: '',
            createdBy: null,
            status: 'open',
            comments: [],
            hasBeenScheduled: false,
            _signature: null
        }
    }

    getWorkflowSteps = (): IFormGroup[] =>
        [{
            label: 'Workflow Settings',
            fields: [
                // @ts-ignore This is to avoid the error of a cyclical data structure comment.reply.reply...
                {
                    label: 'Workflow Name',
                    description: 'Give a name to this Workflow!',
                    key: 'title',
                    type: 'string'
                },
                {
                    label: 'Default Assignee',
                    description: 'By default, all tasks will be assigned to this person. You can always override and re-assign any task to a different person.',
                    key: 'assignee',
                    type: 'system-config'
                },
                {
                    label: 'Select Contract',
                    description: 'Select the contract that this workflow is associated with, else leave it blank.',
                    key: 'contract',
                    type: 'system-config'
                },
                {
                    label: 'Repeats?',
                    description: 'If you want this workflow to repeat, select an option. This workflow will repeat based on the completion date below.',
                    key: 'repeats',
                    type: 'system-config'
                },
                {
                    label: 'Completion Date?',
                    description: 'Select the date in which this workflow should be completed by, if this workflow is reoccurring, select the FIRST date in which this workflow should be completed by.',
                    key: 'dueDate',
                    type: 'date'
                },
                {
                    label: 'Workflow Sign Off?',
                    description: 'Once this workflow is fully completed, do you want someone to sign off on it?',
                    key: 'requireSignOff',
                    type: 'bool'
                }
            ]
        },
        {
            label: 'Create & Assign Tasks',
            fields: [
                {
                    label: 'Workflow Task', key: 'tasks', type: 'list', childFields: [
                        { label: 'Task Name', key: 'tasks.title', description: 'Give a name to this task', type: 'string' },
                        { label: 'Task Description', key: 'tasks.description', description: 'Describe the steps / actions / tasks in detail', type: 'long-form' },
                        {
                            label: 'Task Assignee',
                            key: 'tasks.assignee',
                            description: 'Assign task to user',
                            type: 'system-config',
                            childFields: [
                                {
                                    label: 'Assignee Name',
                                    key: 'assignee',
                                    type: 'system-config'
                                }
                            ]
                        },
                        { label: 'Days to Complete Task', key: 'tasks.daysToComplete', description: 'How many days should the assignee have to complete this task once it\'s assigned to them', type: 'number' },
                        { label: 'Task Require Sign Off', key: 'tasks.requireSignOff', description: 'If you want the task assignee to digitally sign off on this task once completed, select yes.', type: 'bool' },
                        { label: 'Task Dependencies?', key: 'tasks.dependencies', description: 'Does this task have another task that must be completed before this can start?', type: 'multi-system-config' }
                    ]
                }
            ]
        },
        {
            label: 'Review & Activate',
            fields: [
                {
                    label: 'Created At',
                    key: 'createdAt',
                    description: 'Date when the workflow was created',
                    type: 'date'
                },
                {
                    label: 'Expiration Date',
                    key: 'dueDate',
                    type: 'date',
                    description: 'Set an expiration date for the workflow'
                }
            ]
        }]
}

const WorkflowManager = new WorkflowManagerClass()
export default WorkflowManager
